// js/components/Slider.js
import Flickity from 'flickity';

export class Slider extends HTMLElement {
  constructor() {
    super();
    this.flickityInstance = null;
    this.resizeObserver = null;
    this._setupSlider();
  }

  connectedCallback() {
    // Slider is already initialized in constructor
  }

  disconnectedCallback() {
    this.destroy();
  }

  destroy() {
    if (this.flickityInstance) {
      this.flickityInstance.destroy();
      this.flickityInstance = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this._resizeListener) {
      window.removeEventListener("resize", this._resizeListener);
    }
  }

  _setupSlider() {
    const sliderElement = this.querySelector('.slider-content') || this;
    
    if (!sliderElement) return;

    // Get settings from data-settings attribute or individual data attributes
    const settings = this._getSliderSettings();

    // Check if slider should be stackable (no Flickity)
    if (settings.stackable === true || this.dataset.stackable === 'true' || this.classList.contains('product-list--stackable')) {
      return; // Don't initialize Flickity for stackable layouts
    }

    const options = this._getSliderOptions(settings);
    this.flickityInstance = new Flickity(sliderElement, options);
    
    // Setup resize handling
    this._setupResizeHandling();
    
    // Setup ResizeObserver for individual items
    this._setupResizeObserver();
  }

  _getSliderSettings() {
    // Try to get settings from data-settings attribute first
    if (this.dataset.settings) {
      try {
        return JSON.parse(this.dataset.settings);
      } catch (e) {
        console.warn('Invalid JSON in data-settings:', this.dataset.settings);
      }
    }

    // Fallback to individual data attributes
    const settings = {};
    
    if (this.dataset.stackable !== undefined) {
      settings.stackable = this.dataset.stackable === 'true';
    }
    
    if (this.dataset.pageDots !== undefined) {
      settings.pageDots = this.dataset.pageDots === 'true';
    }
    
    if (this.dataset.prevNextButtons !== undefined) {
      settings.prevNextButtons = this.dataset.prevNextButtons === 'true';
    }
    
    if (this.dataset.cellSpacing !== undefined) {
      settings.cellSpacing = parseInt(this.dataset.cellSpacing);
    }
    
    if (this.dataset.draggable !== undefined) {
      settings.draggable = this.dataset.draggable === 'true';
    }
    
    if (this.dataset.groupCells !== undefined) {
      settings.groupCells = this.dataset.groupCells === 'true';
    }

    return settings;
  }

  _getSliderOptions(settings = {}) {
    const defaultOptions = {
      watchCSS: true,
      pageDots: false,
      prevNextButtons: true,
      contain: true,
      resize: false,
      groupCells: true,
      cellAlign: "left",
      draggable: !window.matchMedia("(-moz-touch-enabled: 0), (hover: hover)").matches
    };

    // Apply settings to options
    const customOptions = {};
    
    if (settings.pageDots !== undefined) {
      customOptions.pageDots = settings.pageDots;
    }
    
    if (settings.prevNextButtons !== undefined) {
      customOptions.prevNextButtons = settings.prevNextButtons;
    }
    
    if (settings.cellSpacing !== undefined) {
      customOptions.cellSpacing = settings.cellSpacing;
    }
    
    if (settings.draggable !== undefined) {
      customOptions.draggable = settings.draggable;
    }
    
    if (settings.groupCells !== undefined) {
      customOptions.groupCells = settings.groupCells;
    }

    return { ...defaultOptions, ...customOptions };
  }

  _setupResizeHandling() {
    let lastWidth = window.innerWidth;
    this._resizeListener = () => {
      if (window.innerWidth !== lastWidth) {
        this.flickityInstance.resize();
        lastWidth = window.innerWidth;
      }
    };
    window.addEventListener("resize", this._resizeListener);
  }

  _setupResizeObserver() {
    if (window.ResizeObserver && this.flickityInstance) {
      this.resizeObserver = new ResizeObserver(() => {
        this.flickityInstance.resize();
      });
      
      // Observe all child elements
      this.querySelectorAll('.slider-item, .product-item, .collection-item, .text-with-icons__item').forEach((item) => {
        this.resizeObserver.observe(item);
      });
    }
  }

  // Public methods for external control
  selectCell(index, isWrapped = false, isInstant = false) {
    if (this.flickityInstance && this.flickityInstance.isActive) {
      this.flickityInstance.selectCell(index, isWrapped, isInstant);
    }
  }

  pausePlayer() {
    if (this.flickityInstance && this.flickityInstance.isActive) {
      this.flickityInstance.pausePlayer();
    }
  }

  unpausePlayer() {
    if (this.flickityInstance && this.flickityInstance.isActive) {
      this.flickityInstance.unpausePlayer();
    }
  }

  resize() {
    if (this.flickityInstance) {
      this.flickityInstance.resize();
    }
  }

  // Method to reinitialize slider (useful when content changes)
  reinitialize() {
    this.destroy();
    this._setupSlider();
  }
}

if (!window.customElements.get("cos-flickity")) window.customElements.define("cos-flickity", Slider);
